import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'

/*
 * A bottom sheet, as on a phone (Slack, Android): it rises from the bottom
 * edge, takes the full width, has a grey handle and rounded top corners
 * only. It closes by dragging it down, by tapping outside, with Esc, and
 * with the system "back" (the sheet owns one history entry while open).
 */

const MARK = '__bottomSheet'
const DRAG_CLOSE_PX = 80

export interface BottomSheetControl {
  open: boolean
  /** Opens the sheet and adds the history entry the system "back" will take. */
  show: () => void
  /** Closes it; `then` runs once the history entry is gone (safe to navigate). */
  hide: (then?: () => void) => void
}

/** The state of one sheet; pass the result to `<BottomSheet sheet={…}>`. */
export const useBottomSheet = (): BottomSheetControl => {
  const [open, setOpen] = React.useState(false)
  const openRef = React.useRef(false)
  const pending = React.useRef<(() => void) | null>(null)
  const closing = React.useRef(false)
  const id = React.useId()

  React.useEffect(() => {
    const onPop = () => {
      if (!openRef.current) return
      closing.current = false
      /* Our entry was taken: by "back", or by `hide`. */
      if ((window.history.state as Record<string, unknown> | null)?.[MARK] === id) return
      openRef.current = false
      setOpen(false)
      const then = pending.current
      pending.current = null
      then?.()
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [id])

  const show = React.useCallback(() => {
    if (openRef.current) return
    openRef.current = true
    window.history.pushState({ ...(window.history.state || {}), [MARK]: id }, '', window.location.href)
    setOpen(true)
  }, [id])

  const hide = React.useCallback((then?: () => void) => {
    if (!openRef.current) { then?.(); return }
    /* Already going back: a second `back()` would leave the page. */
    if (closing.current) { if (then) { const prev = pending.current; pending.current = () => { prev?.(); then() } } return }
    if ((window.history.state as Record<string, unknown> | null)?.[MARK] === id) {
      pending.current = then || null
      closing.current = true
      window.history.back()
      return
    }
    openRef.current = false
    setOpen(false)
    then?.()
  }, [id])

  return React.useMemo(() => ({ open, show, hide }), [open, show, hide])
}

interface BottomSheetProps {
  sheet: BottomSheetControl
  /** Read by screen readers only (the sheet shows no title bar). */
  title: string
  children: React.ReactNode
  className?: string
  'data-testid'?: string
}

export const BottomSheet = ({ sheet, title, children, className, ...rest }: BottomSheetProps) => {
  const [drag, setDrag] = React.useState(0)
  const start = React.useRef<number | null>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const opener = React.useRef<HTMLElement | null>(null)

  /* A drag down starts anywhere while the sheet is scrolled to its top. */
  const onTouchStart = (e: React.TouchEvent) => { start.current = (contentRef.current?.scrollTop ?? 0) <= 0 ? e.touches[0]?.clientY ?? null : null }
  const onTouchMove = (e: React.TouchEvent) => {
    if (start.current === null) return
    const dy = (e.touches[0]?.clientY ?? start.current) - start.current
    setDrag(Math.max(0, dy))
  }
  const onTouchEnd = () => {
    if (start.current === null) return
    start.current = null
    if (drag > DRAG_CLOSE_PX) sheet.hide()
    setDrag(0)
  }

  return (
    <DialogPrimitive.Root open={sheet.open} onOpenChange={(o) => { if (!o) sheet.hide() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          ref={contentRef}
          aria-describedby={undefined}
          tabIndex={-1}
          /* Focus moves into the sheet itself (so Tab stays trapped inside and a
             screen reader announces it), not onto its first control: no focus
             ring on a button and no virtual keyboard when a finger opened it. */
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
            contentRef.current?.focus({ preventScroll: true })
          }}
          /* Back to what opened it (the sheet's own trigger is not a Radix one). */
          onCloseAutoFocus={(e) => {
            const target = opener.current
            opener.current = null
            if (target?.isConnected) { e.preventDefault(); target.focus({ preventScroll: true }) }
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={cn(
            'fixed inset-x-0 bottom-0 z-[70] max-h-[85dvh] overflow-y-auto rounded-t-2xl bg-white pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-2xl outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:duration-200',
            className,
          )}
          style={drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}
          data-bottom-sheet
          {...rest}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          {/* The handle: dragging the sheet down closes it. */}
          <div className="flex touch-none justify-center pb-2 pt-3" data-bottom-sheet-handle>
            <span className="h-1.5 w-10 rounded-full bg-gray-300" aria-hidden />
          </div>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
